import { orderedWorkflowVersions } from "./workflowVersions";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PersonRemove } from "@mui/icons-material";

import { isContactWorkflow } from "./workflowParticipants";
import { notifyContactEnrollment } from "./marketingNotifications";
import ContactEnrollmentResults from "./ContactEnrollmentResults";

const versionLabel = (option) =>
  `${option.workflowName} — version ${option.versionNumber} (${option.status})`;

export default function WorkflowOperations({
  workflow,
  version,
  workflows,
  api,
  onChanged,
}) {
  const [source, setSource] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [enrollments, setEnrollments] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState([null]);
  const [search, setSearch] = useState("");
  const [removing, setRemoving] = useState(null);
  const [reason, setReason] = useState("");
  const isOutreach = isContactWorkflow(workflow);
  const [contactSearch, setContactSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactPageInfo, setContactPageInfo] = useState({});
  const [contactPage, setContactPage] = useState(0);
  const [contactCursors, setContactCursors] = useState([null]);
  const [selectedContacts, setSelectedContacts] = useState({});
  const [confirmContacts, setConfirmContacts] = useState(false);
  const [contactResults, setContactResults] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const sources = useMemo(
    () =>
      workflows.flatMap((item) =>
        orderedWorkflowVersions(item)
          .filter(
            (version) =>
              version.status !== "DRAFT" &&
              version.id !== workflow.currentPublishedVersion?.id,
          )
          .map((version) => ({ ...version, workflowName: item.name })),
      ),
    [workflow, workflows],
  );

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const data = await api.getEnrollments(
          workflow.id,
          "active",
          search,
          10,
          cursors[page],
          version?.id,
        );
        if (active) {
          setEnrollments((data.edges || []).map((edge) => edge.node));
          setPageInfo(data.pageInfo || {});
        }
      } catch (requestError) {
        if (active) setError(requestError.message);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [api, workflow.id, search, page, cursors, refreshKey, version?.id]);

  useEffect(() => {
    if (!isOutreach) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const data = await api.searchEligibleContacts({
          workflowId: workflow.id,
          search: contactSearch,
          first: 10,
          after: contactCursors[contactPage],
        });
        if (active) {
          setContacts((data.edges || []).map((edge) => edge.node));
          setContactPageInfo(data.pageInfo || {});
        }
      } catch (requestError) {
        if (active) setError(requestError.message);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [
    api,
    contactCursors,
    contactPage,
    contactSearch,
    isOutreach,
    workflow.id,
    refreshKey,
  ]);

  const enrollContacts = async () => {
    setBusy("enroll-contacts");
    setError("");
    try {
      const response = await api.enrollContacts(
        Object.keys(selectedContacts),
        workflow.id,
        version?.id,
      );
      setContactResults(response);
      notifyContactEnrollment(response);
      setSelectedContacts({});
      setConfirmContacts(false);
      setContactPage(0);
      setContactCursors([null]);
      setRefreshKey((value) => value + 1);
      await onChanged();
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setBusy("");
    }
  };

  const changeSearch = (value) => {
    setSearch(value);
    setPage(0);
    setCursors([null]);
  };
  const nextPage = () => {
    setCursors((items) => {
      const next = [...items];
      next[page + 1] = pageInfo.endCursor;
      return next;
    });
    setPage((value) => value + 1);
  };

  const preflight = async () => {
    if (!source) return;
    setBusy("preview");
    setError("");
    try {
      setPreview(await api.getMigrationPreview(source.id, workflow.id));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy("");
    }
  };
  const migrate = async () => {
    setBusy("migrate");
    setError("");
    try {
      const response = await api.migrateParticipants(source.id, workflow.id);
      if (!response.success) throw new Error(response.message);
      enqueueSnackbar(
        `Enrolled ${response.summary.enrolled}; skipped ${response.summary.alreadyEnrolled} already enrolled participant(s).`,
        { variant: "success" },
      );
      setPreview(null);
      await onChanged();
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setBusy("");
    }
  };
  const removeEnrollment = async () => {
    setBusy("remove");
    try {
      const response = await api.stopEnrollment(removing.id, reason);
      if (!response.success) throw new Error(response.message);
      setEnrollments((items) =>
        items.filter((item) => item.id !== removing.id),
      );
      enqueueSnackbar(
        response.message || "Participant removed from workflow.",
        { variant: "success" },
      );
      setRemoving(null);
      setReason("");
      await onChanged();
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setBusy("");
    }
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {isOutreach && (!version || version.status === "LIVE") && (
        <Paper sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            gap={1}
          >
            <div>
              <Typography variant="h6">Enroll contacts</Typography>
              <Typography color="text.secondary">
                Select verified contacts with confirmed or validated email
                addresses.
              </Typography>
            </div>
            <Button
              variant="contained"
              disabled={!Object.keys(selectedContacts).length}
              onClick={() => setConfirmContacts(true)}
            >
              Enroll selected ({Object.keys(selectedContacts).length})
            </Button>
          </Stack>
          <TextField
            fullWidth
            size="small"
            label="Search contact or school"
            value={contactSearch}
            onChange={(event) => {
              setContactSearch(event.target.value);
              setContactPage(0);
              setContactCursors([null]);
            }}
            sx={{ my: 2 }}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Contact</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Subjects</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        inputProps={{ "aria-label": `Select ${contact.email}` }}
                        checked={Boolean(selectedContacts[contact.id])}
                        onChange={(event) =>
                          setSelectedContacts((items) => {
                            const next = { ...items };
                            if (event.target.checked) next[contact.id] = true;
                            else delete next[contact.id];
                            return next;
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {contact.firstName} {contact.lastName}
                      <Typography variant="caption" display="block">
                        {contact.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {contact.school.name}
                      <Typography variant="caption" display="block">
                        {contact.school.phaseOfEducationDisplay}
                      </Typography>
                    </TableCell>
                    <TableCell>{contact.jobTitle}</TableCell>
                    <TableCell>
                      {contact.subjects
                        .map((subject) => subject.name)
                        .join(", ")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
            <Button
              disabled={contactPage === 0}
              onClick={() => setContactPage((value) => value - 1)}
            >
              Previous
            </Button>
            <Typography alignSelf="center" variant="body2">
              Page {contactPage + 1}
            </Typography>
            <Button
              disabled={!contactPageInfo.hasNextPage}
              onClick={() => {
                setContactCursors((items) => [
                  ...items.slice(0, contactPage + 1),
                  contactPageInfo.endCursor,
                ]);
                setContactPage((value) => value + 1);
              }}
            >
              Next
            </Button>
          </Stack>
          <ContactEnrollmentResults result={contactResults} />
        </Paper>
      )}

      {!isOutreach && workflow.currentPublishedVersion && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Restart / import participants</Typography>
          <Typography color="text.secondary" mb={2}>
            Eligibility is checked for the preview and checked again when you
            confirm. Eligible users start at their first unmet destination stage
            on default version {workflow.currentPublishedVersion.versionNumber}.
            This restarts their journey; use continuation above to preserve the
            pending email.
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <Autocomplete
              fullWidth
              options={sources}
              value={source}
              getOptionLabel={versionLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => {
                setSource(value);
                setPreview(null);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Import from workflow version" />
              )}
            />
            <Button
              onClick={preflight}
              disabled={!source || busy === "preview"}
            >
              Check eligibility
            </Button>
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Active participants</Typography>
        <TextField
          fullWidth
          size="small"
          label="Search by email"
          value={search}
          onChange={(event) => changeSearch(event.target.value)}
          sx={{ my: 2 }}
        />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Participant</TableCell>
                <TableCell>School</TableCell>
                <TableCell>Previous stage completed</TableCell>
                <TableCell>Current stage</TableCell>
                <TableCell>Sender</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrollments.map((enrollment) => (
                <TableRow key={enrollment.id} hover>
                  <TableCell>
                    {enrollment.recipientName}
                    <Typography variant="caption" display="block">
                      {enrollment.recipientEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {enrollment.recipientSchool}
                    <Typography variant="caption" display="block">
                      {enrollment.recipientSchoolPhase}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {enrollment.recipientSubjects
                        ?.map((subject) => subject.name)
                        .join(", ")}
                    </Typography>
                  </TableCell>
                  <TableCell>{enrollment.previousStageCompleted}</TableCell>
                  <TableCell>
                    {enrollment.currentStage?.name || "No current stage"}
                  </TableCell>
                  <TableCell>
                    {enrollment.assignedSender?.displayName || "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove from workflow">
                      <IconButton
                        aria-label={`Remove ${enrollment.recipientEmail} from workflow`}
                        onClick={() => setRemoving(enrollment)}
                      >
                        <PersonRemove />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!enrollments.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No active participants.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
          <Button disabled={page === 0} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <Typography alignSelf="center" variant="body2">
            Page {page + 1}
          </Typography>
          <Button disabled={!pageInfo.hasNextPage} onClick={nextPage}>
            Next
          </Button>
        </Stack>
      </Paper>

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)}>
        <DialogTitle>Confirm participant import</DialogTitle>
        <DialogContent>
          {preview && (
            <Stack spacing={1} mt={1}>
              <Typography>
                {preview.considered} participants considered
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip color="success" label={`${preview.eligible} eligible`} />
                <Chip label={`${preview.active} active`} />
                <Chip label={`${preview.completed} completed`} />
                <Chip label={`${preview.alreadyEnrolled} already enrolled`} />
              </Stack>
              {Object.entries(preview.excluded || {}).map(([key, total]) => (
                <Typography key={key} variant="body2">
                  {key.replaceAll("_", " ")}: {total}
                </Typography>
              ))}
              <Alert severity="info">
                Eligibility will be checked again before each participant is
                enrolled.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={migrate}
            disabled={busy === "migrate"}
          >
            Confirm import
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)}>
        <DialogTitle>Remove participant from workflow?</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            {removing?.recipientEmail} will receive no further automated emails
            from this workflow. This does not globally unsubscribe them.
          </Typography>
          <TextField
            fullWidth
            label="Internal reason (optional)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button color="error" onClick={removeEnrollment}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmContacts} onClose={() => setConfirmContacts(false)}>
        <DialogTitle>Enroll selected contacts?</DialogTitle>
        <DialogContent>
          Eligibility will be checked again for each of the{" "}
          {Object.keys(selectedContacts).length} selected contacts.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmContacts(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy === "enroll-contacts"}
            onClick={enrollContacts}
          >
            Enroll contacts
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
